import {
  RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import SignatureCanvas from 'react-signature-canvas';
import isNil from 'lodash/isNil';
import {
  useFormContext,
} from 'react-hook-form';
import {
  FormFieldsData,
} from 'views/EMR/components/FillableForm/types';

type PrepFieldName = 'signature' | 'initial';

type PrepFieldConfig = {
  drawField: 'signatureDraw' | 'initialDraw';
  typedField: 'signatureTyped' | 'initialTyped';
  canonicalField: 'signature' | 'initial';
  inputId: string;
  typePlaceholder: string;
};

const fieldConfigByName: Record<PrepFieldName, PrepFieldConfig> = {
  signature: {
    drawField: 'signatureDraw',
    typedField: 'signatureTyped',
    canonicalField: 'signature',
    inputId: 'signature-input',
    typePlaceholder: 'Type your signature...',
  },
  initial: {
    drawField: 'initialDraw',
    typedField: 'initialTyped',
    canonicalField: 'initial',
    inputId: 'initial-input',
    typePlaceholder: 'Type your initials...',
  },
};

function updateCanvasWidth(
  boxRef: RefObject<HTMLDivElement | null>,
  setCanvasWidth: (width: number) => void,
) {
  const el = boxRef.current;
  if (!isNil(el)) {
    const boxWidth = el.clientWidth ?? 0;
    const maxPreferred = 420;
    const minWidth = 180;
    const target = Math.min(boxWidth, maxPreferred);
    setCanvasWidth(Math.max(minWidth, target));
  }
}

export default function useSignaturePrepField(fieldName: PrepFieldName) {
  const { setValue, watch } = useFormContext<FormFieldsData>();
  const config = fieldConfigByName[fieldName];

  const [tab, setTab] = useState<'draw' | 'type'>('type');
  const [drawingData, setDrawingData] = useState<unknown>(null);
  const padRef = useRef<SignatureCanvas | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const [canvasWidth, setCanvasWidth] = useState<number>(360);
  const [interacted, setInteracted] = useState<boolean>(false);
  const hasInitializedSync = useRef(false);

  const isDrawing = tab === 'draw';
  const isTyping = tab === 'type';
  const canvasHeight = useMemo(
    () => (canvasWidth < 320 ? 80 : 120),
    [canvasWidth],
  );

  const drawValue = watch(config.drawField);
  const typedValue = watch(config.typedField);

  useLayoutEffect(() => {
    updateCanvasWidth(boxRef, setCanvasWidth);
    const raf = window.requestAnimationFrame(() => {
      updateCanvasWidth(boxRef, setCanvasWidth);
    });
    return () => { window.cancelAnimationFrame(raf); };
  }, [tab]);

  useEffect(() => {
    const selected = isDrawing ? (drawValue ?? '') : (typedValue ?? '');
    if (!hasInitializedSync.current) {
      hasInitializedSync.current = true;
      setValue(config.canonicalField, selected, { shouldValidate: false });
    } else {
      setValue(config.canonicalField, selected, {
        shouldValidate: false,
        shouldTouch: false,
        shouldDirty: false,
      });
    }
  }, [tab, drawValue, typedValue, isDrawing, setValue, config.canonicalField]);

  function onDraw(data: string) {
    setValue(config.drawField, data, { shouldValidate: true });
  }

  function onType(value: string) {
    setValue(config.typedField, value, { shouldValidate: true });
  }

  function onClear() {
    if (isDrawing) {
      padRef.current?.clear();
      setValue(config.drawField, '', { shouldValidate: true });
      setDrawingData(null);
    } else {
      setValue(config.typedField, '', { shouldValidate: true });
    }
  }

  return {
    tab,
    setTab,
    padRef,
    boxRef,
    canvasWidth,
    canvasHeight,
    typedValue: typedValue ?? '',
    drawingData,
    setDrawingData,
    interacted,
    setInteracted,
    isDrawing,
    isTyping,
    onDraw,
    onType,
    onClear,
    inputId: config.inputId,
    typePlaceholder: config.typePlaceholder,
  };
}
